import { Link, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard, MessageSquare, Bot, BookOpen, Smartphone,
  Users, BarChart3, CreditCard, Settings, LogOut, ChevronDown,
  FlaskConical, Megaphone, Contact, FileText, Tag, Key
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ROUTES } from '@/config/routes';

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
}

interface NavGroup {
  label: string | null;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: null,
    items: [
      { path: ROUTES.DASHBOARD, label: 'Dashboard', icon: LayoutDashboard },
      { path: ROUTES.CONVERSATIONS, label: 'Conversations', icon: MessageSquare },
    ],
  },
  {
    label: 'Messaging',
    items: [
      { path: ROUTES.TEMPLATES, label: 'Templates', icon: FileText },
      { path: ROUTES.CAMPAIGNS, label: 'Campaigns', icon: Megaphone },
    ],
  },
  {
    label: 'AI & Knowledge',
    items: [
      { path: ROUTES.AGENT_CONFIG, label: 'AI Agent', icon: Bot },
      { path: ROUTES.PLAYGROUND, label: 'AI Playground', icon: FlaskConical },
      { path: ROUTES.KNOWLEDGE_BASE, label: 'Knowledge Base', icon: BookOpen },
    ],
  },
  {
    label: 'Audience',
    items: [
      { path: ROUTES.CONTACTS, label: 'Contacts', icon: Contact },
      { path: ROUTES.TAGS, label: 'Tags', icon: Tag },
    ],
  },
  {
    label: 'Operations',
    items: [
      { path: ROUTES.WHATSAPP, label: 'WhatsApp', icon: Smartphone },
      { path: ROUTES.TEAM, label: 'Team', icon: Users },
      { path: ROUTES.ANALYTICS, label: 'Analytics', icon: BarChart3 },
    ],
  },
  {
    label: 'Account',
    items: [
      { path: ROUTES.API_KEYS, label: 'API Keys', icon: Key },
      { path: ROUTES.BILLING, label: 'Billing', icon: CreditCard },
      { path: ROUTES.SETTINGS, label: 'Settings', icon: Settings },
    ],
  },
];

interface SidebarNavProps {
  onItemClick?: () => void;
}

function SidebarNav({ onItemClick }: SidebarNavProps) {
  const location = useLocation();
  return (
    <nav className="px-3 space-y-4">
      {navGroups.map((group, gi) => (
        <div key={gi}>
          {group.label && (
            <p className="px-3 mb-1.5 text-[10px] font-semibold text-zinc-600 uppercase tracking-wider">{group.label}</p>
          )}
          <div className="space-y-0.5">
            {group.items.map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => onItemClick?.()}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50'
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="text-[13px] font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

interface SidebarProps {
  tenant: { name?: string; email?: string } | null;
  user: { name?: string; email?: string; profilePicture?: string | null } | null;
  onLogout: () => void;
}

export default function Sidebar({ tenant, user, onLogout }: SidebarProps) {
  const initials = (tenant?.name || 'A').charAt(0).toUpperCase();

  return (
    <aside className="hidden lg:flex flex-col w-60 bg-zinc-950 border-r border-zinc-800 shrink-0">
      <div className="h-16 flex items-center px-5 border-b border-zinc-800">
        <Link to={ROUTES.DASHBOARD} className="flex items-center gap-2.5" data-testid="sidebar-logo">
          <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
            <Bot className="w-5 h-5 text-emerald-950" />
          </div>
          <span className="font-bold text-lg tracking-tight font-[Manrope]">Agentick</span>
        </Link>
      </div>
      <ScrollArea className="flex-1 py-4">
        <SidebarNav />
      </ScrollArea>
      <div className="p-3 border-t border-zinc-800">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-zinc-800/50 transition-colors text-left"
              data-testid="user-menu-trigger"
            >
              <Avatar className="w-8 h-8">
                {user?.profilePicture && (
                  <AvatarImage src={user.profilePicture} alt={user?.name || tenant?.name} className="object-cover" />
                )}
                <AvatarFallback className="bg-emerald-500/20 text-emerald-400 text-xs font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-100 truncate">{tenant?.name || 'My Business'}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email || tenant?.email || ''}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={8} className="w-52 bg-zinc-900 border-zinc-800">
            <DropdownMenuItem className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-100 cursor-pointer" disabled>
              <Settings className="mr-2 h-4 w-4" /> Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-zinc-800" />
            <DropdownMenuItem
              onClick={onLogout}
              className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer"
              data-testid="logout-btn"
            >
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}

export { SidebarNav };
